import { forwardRef } from "react";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { SquareButton, type SquareButtonProps } from "mds/components/buttons/SquareButton";
import { AddPlusIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectIsEditModeEnabled } from "store/selectors";

type AddReference =
  | "page"
  | "block"
  | "activity"
  | "topic"
  | "assignment"
  | "section_description"
  | "course_resource"
  | "ingestion"
  | "instructor_workspace"
  | "outcome"
  | "outcome_subgroup"
  | "outcome_group"
  | "outcome_description"
  | "page_group";

export type HideUnlessEditModeButtonProps = Omit<SquareButtonProps, "kind" | "size"> & {
  reference: AddReference;
  isDisabled?: boolean;
};

export const HideUnlessEditModeButton = forwardRef<
  HTMLButtonElement,
  HideUnlessEditModeButtonProps
>(function HideUnlessEditModeButton(
  { onClick, reference, className, eventName, eventProps, isDisabled, ...rest },
  ref,
) {
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  if (!isEditModeEnabled) {
    return null;
  }

  const buttonTitle = t(`common.add.${reference}`);

  return (
    <SquareButton
      className={className}
      disabled={isDisabled}
      eventName={eventName}
      eventProps={eventProps}
      kind="link"
      ref={ref}
      size="s"
      title={buttonTitle}
      onClick={onClick}
      {...rest}
    >
      <IconText iconStart={<AddPlusIcon />} text={buttonTitle} />
    </SquareButton>
  );
});
